import classNames from 'classnames';
import React from 'react';
import * as styles from './CommentThreadCompactAvatarStack.module.scss';
import type { ObjectWithId } from '../../graphql/generated/graphql';

interface OverflowCountProps {
  overflowCount: number;
  variant?: 'accent' | 'success';
}

export const OverflowCount = ({
  overflowCount,
  variant = 'accent',
}: OverflowCountProps) => {
  return (
    <div
      className={classNames(styles.counter, {
        [styles.success]: variant === 'success',
        [styles.accent]: variant === 'accent',
      })}
    >
      {overflowCount}
    </div>
  );
};

interface CommentThreadCompactAvatarStackProps<Items extends ObjectWithId[]> {
  items: Items;
  children: (author: Items[number]) => React.ReactNode;
  minOverflow?: number;
  maxAvatarsShown?: number;
  renderOverflowCount?: (overflowCount: number) => React.ReactNode;
}

const MAX_AVATARS_SHOWN = 3;
const DEFAULT_MIN_OVERFLOW = 2;

const CommentThreadCompactAvatarStack = <Items extends ObjectWithId[]>({
  items,
  children,
  maxAvatarsShown = MAX_AVATARS_SHOWN,
  minOverflow = DEFAULT_MIN_OVERFLOW,
  renderOverflowCount = (overflowCount) => (
    <OverflowCount overflowCount={overflowCount} />
  ),
}: CommentThreadCompactAvatarStackProps<Items>) => {
  const overflowCount = items.length - (maxAvatarsShown - 1);

  const slicedItems = [
    ...items.slice(0, 2),
    ...(items.length > 2 ? items.slice(-1) : []),
  ];

  return (
    <div className={styles.container}>
      {slicedItems.map((item, i) => {
        if (overflowCount >= minOverflow && i === 1) {
          return (
            <React.Fragment key="overflow-count">
              <div className={styles.avatarContainer}>
                {renderOverflowCount(overflowCount)}
              </div>
            </React.Fragment>
          );
        }

        return (
          <div className={styles.avatarContainer} key={item.id}>
            {children(item)}
          </div>
        );
      })}
    </div>
  );
};

export default CommentThreadCompactAvatarStack;
